import React, { useEffect, useState } from 'react';
import { Search, Clock, User, Truck, Users, Settings, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Activity type configuration
const ACTIVITY_TYPES = {
  vehicle_status: { icon: Truck, label: 'Véhicule', color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  user_created: { icon: Users, label: 'Utilisateur créé', color: 'text-green-500', bgColor: 'bg-green-500/10' },
  user_updated: { icon: Users, label: 'Utilisateur modifié', color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  user_deleted: { icon: Users, label: 'Utilisateur supprimé', color: 'text-red-500', bgColor: 'bg-red-500/10' },
  employee_created: { icon: User, label: 'Salarié créé', color: 'text-green-500', bgColor: 'bg-green-500/10' },
  employee_updated: { icon: User, label: 'Salarié modifié', color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  employee_deleted: { icon: User, label: 'Salarié supprimé', color: 'text-red-500', bgColor: 'bg-red-500/10' },
  grade_changed: { icon: Users, label: 'Grade modifié', color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  settings_changed: { icon: Settings, label: 'Paramètres', color: 'text-gray-500', bgColor: 'bg-gray-500/10' },
};

// Format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Activity Entry Component - Compact version
const ActivityEntry = ({ activity, theme }) => {
  const [expanded, setExpanded] = useState(false);
  const typeConfig = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.settings_changed;
  const Icon = typeConfig.icon;

  return (
    <div className={cn(
      "rounded-xl border transition-all",
      theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
    )}>
      <div 
        className={cn(
          "p-3 cursor-pointer",
          theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        )}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Top - Icon + Description */}
        <div className="flex items-start gap-3 mb-2">
          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", typeConfig.bgColor)}>
            <Icon size={16} className={typeConfig.color} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={cn("text-sm font-medium leading-tight", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              {activity.description}
            </p>
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full mt-1 inline-block", typeConfig.bgColor, typeConfig.color)}>
              {typeConfig.label}
            </span>
          </div>
          {activity.details && (
            expanded ? (
              <ChevronUp size={16} className={cn("shrink-0", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')} />
            ) : (
              <ChevronDown size={16} className={cn("shrink-0", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')} />
            )
          )}
        </div>

        {/* Bottom - Date left, Author right */}
        <div className="flex items-center justify-between text-[10px] pl-11">
          <div className={cn("flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
            <Clock size={10} />
            {formatDateTime(activity.created_at)}
          </div>
          <span className={cn(theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
            par {activity.user_name}
          </span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && activity.details && (
        <div className={cn(
          "px-3 pb-3 border-t",
          theme === 'dark' ? 'border-white/10' : 'border-gray-100'
        )}>
          <div className="mt-2 pl-11">
            <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-300' : 'text-gray-600')}>
              {activity.details}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ActivityLog() {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const { theme } = useTheme();

  useEffect(() => {
    fetchActivities();
  }, []);

  useEffect(() => {
    let filtered = activities;
    
    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.user_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.type === typeFilter);
    }
    
    setFilteredActivities(filtered);
  }, [searchTerm, activities, typeFilter]);

  const fetchActivities = async () => {
    try {
      const response = await api.get('/activity-log');
      // Sort by date descending (most recent first)
      const sorted = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setActivities(sorted);
      setFilteredActivities(sorted);
    } catch (error) {
      console.error('Error fetching activity log:', error);
      // Show empty state if endpoint returns error
      setActivities([]);
      setFilteredActivities([]);
      toast.error('Impossible de charger l\'historique');
    } finally {
      setLoading(false);
    }
  };

  // Count by type
  const countByType = (type) => activities.filter(a => a.type === type).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-1 py-4 md:p-0 pb-24 md:pb-0" data-testid="activity-log-page">
      {/* Header */}
      <div className="flex flex-col items-center gap-4">
        <h1 className={cn("text-2xl font-bold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
          Historique des modifications
        </h1>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              "pl-9 h-10",
              theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-200'
            )}
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Type Filter */}
      <div className={cn(
        "flex flex-wrap items-center justify-center gap-3 p-4 rounded-xl border",
        theme === 'dark' ? 'bg-zinc-900/30 border-white/5' : 'bg-gray-50 border-gray-200'
      )}>
        <Filter size={16} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
        
        <button
          onClick={() => setTypeFilter('all')}
          className={cn(
            "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
            typeFilter === 'all'
              ? 'bg-red-500 text-white'
              : theme === 'dark' ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
        >
          Tout ({activities.length})
        </button>

        {Object.entries(ACTIVITY_TYPES).slice(0, 4).map(([type, config]) => {
          const count = countByType(type);
          if (count === 0) return null;
          return (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5",
                typeFilter === type
                  ? `${config.bgColor} ${config.color}`
                  : theme === 'dark' ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {config.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Count */}
      <p className={cn("text-sm text-center", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
        {filteredActivities.length} activité{filteredActivities.length > 1 ? 's' : ''}
      </p>

      {/* Activity List */}
      <div className="space-y-4 max-w-4xl mx-auto">
        {filteredActivities.map((activity) => (
          <ActivityEntry
            key={activity.id}
            activity={activity}
            theme={theme}
          />
        ))}
      </div>

      {filteredActivities.length === 0 && (
        <div className={cn(
          "text-center py-12 rounded-xl border max-w-4xl mx-auto",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10 text-zinc-500' : 'bg-white border-gray-200 text-gray-500'
        )}>
          Aucune activité trouvée
        </div>
      )}
    </div>
  );
}
